import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, Pencil, History, TrendingUp, TrendingDown, Minus, Search, ArrowUp, ArrowDown, ArrowUpDown, Wand2, FileText, Mail, Phone, Copy, Info } from "lucide-react";
import QuotaBar from "../components/QuotaBar";
import PageHero from "../components/PageHero";
import SafeImage from "../components/SafeImage";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { api } from "../lib/api";
import ProductDetail from "../components/ProductDetail";
import DuplicateDialog from "../components/DuplicateDialog";
import DuplicateReviewDialog from "../components/DuplicateReviewDialog";
import { eur, eurHT, pct, dateFR, UNITS, CATEGORIES, CATEGORY_ORDER, canonicalCategory, VAT_RATES, ALLERGENS, unitLabel } from "../lib/format";
import { CategoryIcon } from "../components/CategoryIcon";
import { ProductDeleteDialog } from "../components/ProductDeleteDialog";

const CANONICAL_ORDER = [
  "Viandes", "Produits de la mer", "Fruits et légumes", "Crèmerie",
  "Épicerie", "Vins", "Spiritueux", "Boissons", "Divers",
];
// Nom court affiché dans les onglets pour tenir sur une seule ligne.
const CANONICAL_SHORT = {
  "Viandes": "Viandes",
  "Produits de la mer": "Mer",
  "Fruits et légumes": "F & L",
  "Crèmerie": "Crèmerie",
  "Épicerie": "Épicerie",
  "Vins": "Vins",
  "Spiritueux": "Spiritueux",
  "Boissons": "Boissons",
  "Divers": "Divers",
};

function Spark({ values, color }) {
  if (!values || values.length < 2) return null;
  // Feb 2026 §Chart-scale — même règle que le graphique détaillé :
  //  - amplitude minimale = 20 % du prix médian → une variation < 20 %
  //    apparaît discrète, jamais projetée sur toute la hauteur du sparkline.
  //  - cas plat (min ≈ max) → ligne horizontale au milieu.
  //  - fourchette centrée autour du milieu (mid ± amplitude/2).
  const min = Math.min(...values);
  const max = Math.max(...values);
  const rangeRaw = max - min;
  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted.length % 2
    ? sorted[(sorted.length - 1) / 2]
    : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
  const anchor = median || max || 1;
  const minAmplitude = Math.abs(anchor) * 0.20;
  const isFlat = rangeRaw < 1e-6;
  const effectiveRange = Math.max(rangeRaw, minAmplitude, 1e-6);
  const mid = (min + max) / 2;
  const yMin = mid - effectiveRange / 2;
  const yRange = Math.max(effectiveRange, 1e-6);
  const w = 64, h = 22;
  const yFor = (v) => isFlat ? h / 2 : h - ((v - yMin) / yRange) * (h - 4) - 2;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${yFor(v)}`).join(" ");
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      {values.map((v, i) => (
        <circle key={i} cx={(i / (values.length - 1)) * w} cy={yFor(v)} r={i === values.length - 1 ? 2.5 : 1.5} fill={color} />
      ))}
    </svg>
  );
}

// En-tête cliquable de la table Produits. Cycle asc → desc → aucun.
// Reste focusable au clavier et annonce l'état de tri courant (aria-sort).
function SortableTh({ label, k, align, sortKey, sortDir, onToggle }) {
  const active = sortKey === k;
  const dir = active ? sortDir : null;
  const Icon = dir === "asc" ? ArrowUp : dir === "desc" ? ArrowDown : ArrowUpDown;
  const ariaSort = dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none";
  return (
    <th className={align === "num" ? "num" : undefined} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onToggle(k)}
        data-testid={`product-sort-${k}`}
        className={`inline-flex items-center gap-1 ${align === "num" ? "justify-end w-full" : ""} font-medium hover:text-[#1769E0] transition-colors ${active ? "text-[#1769E0]" : "text-inherit"}`}
      >
        {label}
        <Icon className={`w-3 h-3 shrink-0 ${active ? "opacity-100" : "opacity-40"}`} strokeWidth={2.5} />
      </button>
    </th>
  );
}

const EMPTY = {
  name: "", category: "Viandes", supplier_id: "", supplier_ref: "",
  purchase_price: 0, price_is_ttc: false, vat_rate: 5.5,
  purchase_quantity: 1, purchase_unit: "kg", pack_units: 1,
  yield_percent: 100, loss_percent: 0,
  reference_price: 0, alert_threshold_pct: 10,
  allergens: [],
};

// Feb 2026 — Normalisation des alias d'unité vers une valeur EXACTE
// acceptée par le Select `UNITS`. Un produit peut porter en base des
// variantes historiques ("unité", "Unité", "pièce", "U", …) que le Select
// ne reconnaît pas → il s'affiche vide et bloque toute sauvegarde. On
// ramène toujours vers l'une des valeurs canoniques, jamais un vide.
const UNIT_ALIASES = {
  u: "u", U: "u", unité: "u", "Unité": "u", unite: "u", Unite: "u",
  UNITE: "u", pièce: "u", piece: "u", "Pièce": "u", Piece: "u",
  pce: "u", pcs: "u", pc: "u",
  kg: "kg", Kg: "kg", KG: "kg", kilo: "kg", kilos: "kg", kilogramme: "kg",
  g: "g", G: "g", gr: "g", gramme: "g", grammes: "g",
  L: "L", l: "L", litre: "L", Litre: "L", LITRE: "L",
  cl: "cl", CL: "cl", Cl: "cl",
  ml: "ml", ML: "ml", Ml: "ml",
  bouteille: "bouteille", Bouteille: "bouteille",
  botte: "botte", Botte: "botte",
  carton: "carton", Carton: "carton",
  Mesure: "Mesure", mesure: "Mesure",
};
function normalizeUnitToSelect(u) {
  if (u == null) return "u";
  const raw = String(u).trim();
  if (!raw) return "u";
  if (UNIT_ALIASES[raw]) return UNIT_ALIASES[raw];
  if (UNITS.includes(raw)) return raw;
  const lower = raw.toLowerCase();
  if (UNIT_ALIASES[lower]) return UNIT_ALIASES[lower];
  return "u";
}

// Feb 2026 — Diff strict avant PATCH. Envoie SEULEMENT les champs dont la
// valeur normalisée diffère de l'état initial → un simple changement de
// nom/catégorie/allergènes n'entraîne PAS un `price_changed=True` côté
// backend, donc pas de nouveau point d'historique.
function diffForm(current, initial) {
  const out = {};
  const keys = new Set([...Object.keys(current), ...Object.keys(initial)]);
  for (const k of keys) {
    const a = current[k];
    const b = initial[k];
    if (Array.isArray(a) || Array.isArray(b)) {
      const aa = Array.isArray(a) ? [...a].sort() : [];
      const bb = Array.isArray(b) ? [...b].sort() : [];
      if (JSON.stringify(aa) !== JSON.stringify(bb)) out[k] = a;
      continue;
    }
    if (typeof a === "number" || typeof b === "number") {
      if (Number(a || 0) !== Number(b || 0)) out[k] = a;
      continue;
    }
    if ((a ?? "") !== (b ?? "")) out[k] = a;
  }
  return out;
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  // Feb 2026 — Snapshot immuable du formulaire normalisé à l'ouverture.
  // Permet un diff strict avant PATCH : un simple changement de
  // nom/catégorie/allergènes n'envoie PAS les champs prix → pas de
  // nouveau point historique par erreur.
  const [initialForm, setInitialForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  // Feb 2026 — Dossier de négociation par produit (bouton par ligne).
  const [productDossier, setProductDossier] = useState(null);
  const [productDossierLoading, setProductDossierLoading] = useState(false);
  const openProductDossier = async (pid) => {
    setProductDossierLoading(true);
    try {
      const d = await api.negotiation(pid);
      setProductDossier(d);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "negotiation_locked") {
        toast.error(detail.message || "Fonction verrouillée sur votre formule.");
      } else {
        toast.error("Impossible d'ouvrir le dossier de négociation.");
      }
    } finally { setProductDossierLoading(false); }
  };
  const copyText = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copié dans le presse-papier"); };
  const [duplicates, setDuplicates] = useState(null); // { candidates, draft }
  const [reviewPairs, setReviewPairs] = useState(null); // { open, pairs } — nouveau flow safe
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  const [search, setSearch] = useState("");
  // Ouverture directe d'une fiche produit via `?open=<id>` (utilisé
  // depuis Fournisseurs / Dossiers de négociation).
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setDetailId(openId);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Tri : clic sur un en-tête cycle asc → desc → aucun. `key` = clé de tri.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null); // 'asc' | 'desc' | null
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null); setSortDir(null);
  };

  // Sync when the URL query changes (e.g. arriving from Today cards).
  useEffect(() => {
    const q = searchParams.get("filter") || "all";
    if (q !== filter) setFilter(q);
  }, [searchParams]);
  const setFilterAndUrl = (k) => {
    setFilter(k);
    if (k === "all") searchParams.delete("filter"); else searchParams.set("filter", k);
    setSearchParams(searchParams, { replace: true });
  };

  const load = async () => {
    const [p, s] = await Promise.all([api.products(), api.suppliers()]);
    setProducts(p); setSuppliers(s);
  };
  useEffect(() => { load(); }, []);
  // Auto-refresh instantané après validation d'une facture, création
  // ou suppression d'un produit — les filtres se recomputent live.
  useEffect(() => {
    const h = (e) => { if (!e?.detail?.resource || e.detail.resource === "products") load(); };
    window.addEventListener("rentascan:usage-changed", h);
    window.addEventListener("rentascan:invoice-validated", h);
    return () => {
      window.removeEventListener("rentascan:usage-changed", h);
      window.removeEventListener("rentascan:invoice-validated", h);
    };
  }, []);

  const openNew = () => { setForm(EMPTY); setInitialForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    // Feb 2026 §« Bouton Enregistrer cassé Burratina » — On initialise
    // TOUJOURS le formulaire depuis la référence CANONIQUE du produit
    // (`ref_price_per_base` + `ref_base_unit`), jamais depuis l'ancien
    // colisage OCR (49,20 € pour 24 pièces). Cadrage 1×1 :
    //   * purchase_price   = prix HT par UNITÉ canonique (p.ex. 2,05 €/pièce)
    //   * purchase_quantity= 1
    //   * pack_units       = 1
    //   * purchase_unit    = alias normalisé (u/kg/L) — jamais un Select vide
    //   * reference_price  = 0 (à re-renseigner dans ce cadrage — ne pas
    //                            réutiliser un ancien TOTAL comme cible unitaire)
    // Fallback : si aucune référence canonique n'est disponible, on
    // conserve les champs bruts existants (édition d'un produit purement
    // manuel jamais lié à une facture).
    const hasCanonical = p.ref_price_per_base != null && p.ref_base_unit;
    const normalized = hasCanonical
      ? {
          ...p,
          purchase_price: Number(p.ref_price_per_base) || 0,
          price_is_ttc: false,
          purchase_quantity: 1,
          pack_units: 1,
          purchase_unit: normalizeUnitToSelect(p.ref_base_unit),
          reference_price: 0,
          supplier_id: p.supplier_id || "",
          allergens: p.allergens || [],
          vat_rate: p.vat_rate ?? 5.5,
          yield_percent: p.yield_percent ?? 100,
          loss_percent: p.loss_percent ?? 0,
          alert_threshold_pct: p.alert_threshold_pct ?? 10,
        }
      : {
          ...p,
          purchase_price: Number(p.purchase_price) || 0,
          price_is_ttc: !!p.price_is_ttc,
          purchase_quantity: Number(p.purchase_quantity) || 1,
          pack_units: Number(p.pack_units) || 1,
          purchase_unit: normalizeUnitToSelect(p.purchase_unit || "kg"),
          reference_price: p.reference_price ?? 0,
          supplier_id: p.supplier_id || "",
          allergens: p.allergens || [],
          vat_rate: p.vat_rate ?? 5.5,
          yield_percent: p.yield_percent ?? 100,
          loss_percent: p.loss_percent ?? 0,
          alert_threshold_pct: p.alert_threshold_pct ?? 10,
        };
    setForm(normalized);
    setInitialForm(normalized);
    setEditId(p.id);
    setOpen(true);
  };

  const toggleAllergen = (code) => {
    const cur = form.allergens || [];
    setForm({ ...form, allergens: cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code] });
  };

  const save = async (force = false) => {
    if (saving) return; // protection double-clic
    if (!form.name) { toast.error("Le nom du produit est requis"); return; }
    // Prix obligatoire uniquement à la CRÉATION (édition d'un produit
    // sans changement de prix doit rester possible sans re-saisir le prix).
    if (!editId && !Number(form.purchase_price)) {
      toast.error("Le prix d'achat est requis pour créer un produit");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        // Édition : diff strict → n'envoie QUE les champs modifiés.
        const diff = diffForm(form, initialForm);
        if (Object.keys(diff).length === 0) {
          toast.info("Aucune modification à enregistrer");
          setOpen(false);
          return;
        }
        // Feb 2026 §« payload cohérent quand le prix change » — Si N'IMPORTE
        // QUEL champ prix change, on envoie l'ensemble complet des inputs
        // prix (cadrage 1×1 normalisé), sinon le backend recombinerait
        // avec l'ancien `purchase_quantity=24` OCR et le prix serait divisé.
        const PRICE_FIELDS = ["purchase_price", "price_is_ttc", "vat_rate",
          "purchase_quantity", "purchase_unit", "pack_units"];
        const priceTouched = PRICE_FIELDS.some((k) => k in diff);
        if (priceTouched) {
          for (const k of PRICE_FIELDS) diff[k] = form[k];
        }
        // Coerce numériques uniquement pour les champs présents dans le diff.
        const numFields = ["purchase_price", "purchase_quantity", "pack_units",
          "yield_percent", "loss_percent", "vat_rate", "reference_price",
          "alert_threshold_pct"];
        const payload = { ...diff };
        for (const k of numFields) {
          if (k in payload) {
            const v = Number(payload[k]);
            payload[k] = k === "reference_price" ? (v || null) : v;
          }
        }
        if ("supplier_id" in payload) payload.supplier_id = payload.supplier_id || null;
        if ("allergens" in payload) payload.allergens = payload.allergens || [];
        await api.updateProduct(editId, payload);
        toast.success("Produit mis à jour");
      } else {
        // Création : payload complet.
        const payload = {
          ...form,
          purchase_price: Number(form.purchase_price),
          purchase_quantity: Number(form.purchase_quantity),
          pack_units: Number(form.pack_units),
          yield_percent: Number(form.yield_percent),
          loss_percent: Number(form.loss_percent),
          vat_rate: Number(form.vat_rate),
          reference_price: Number(form.reference_price) || null,
          alert_threshold_pct: Number(form.alert_threshold_pct),
          allergens: form.allergens || [],
          supplier_id: form.supplier_id || null,
        };
        await api.createProduct(payload, force);
        toast.success("Produit ajouté");
      }
      window.dispatchEvent(new CustomEvent("rentascan:usage-changed", { detail: { resource: "products" } }));
      setOpen(false); setDuplicates(null); await load();
    } catch (e) {
      if (!editId && e.response?.status === 409 && e.response?.data?.detail?.candidates) {
        setDuplicates({
          candidates: e.response.data.detail.candidates,
          draft: form,
        });
        return;
      }
      // Feb 2026 — Erreur backend LISIBLE, pas un toast générique.
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "string" && detail)
        || detail?.message
        || detail?.error
        || e?.message
        || "Erreur d'enregistrement";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const p = pendingDelete;
    setPendingDelete(null);
    try {
      const res = await api.deleteProduct(p.id);
      const n = res.recipes_updated?.length || 0;
      toast.success(n ? `Produit supprimé et retiré de ${n} recette${n > 1 ? "s" : ""}` : "Produit supprimé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
      return;
    }
    window.dispatchEvent(new CustomEvent("rentascan:usage-changed", { detail: { resource: "products" } }));
    await load();
  };

  // Filtres dynamiques : « Tous » + une entrée par catégorie canonique
  // présente au moins une fois dans le catalogue. Les 9 catégories sont
  // fixes ; celles qui ne contiennent aucun produit ne sont pas affichées.
  const filters = useMemo(() => {
    const counts = products.reduce((acc, p) => {
      const c = canonicalCategory(p.category);
      acc[c] = (acc[c] || 0) + 1;
      return acc;
    }, {});
    const total = products.length;
    const catFilters = CANONICAL_ORDER
      .filter((c) => (counts[c] || 0) > 0)
      .map((c) => ({
        key: `cat_${c}`,
        category: c,
        label: CANONICAL_SHORT[c],
        count: counts[c],
        match: (p) => canonicalCategory(p.category) === c,
      }));
    return [
      { key: "all", category: null, label: "Tous", count: total, match: () => true },
      ...catFilters,
    ];
  }, [products]);

  const filteredProducts = useMemo(() => {
    const f = filters.find((x) => x.key === filter) || filters[0];
    const q = search.trim().toLowerCase();
    const list = products.filter((p) => f.match(p) && (
      !q || (p.name || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q)
    ));
    if (!sortKey || !sortDir) return list;
    const sup = new Map(suppliers.map((s) => [s.id, s.name]));
    const val = (p) => {
      const t = p.trend || {};
      switch (sortKey) {
        case "name": return (p.name || "").toLowerCase();
        case "supplier": return (t.curr_supplier_name || sup.get(p.supplier_id) || "").toLowerCase();
        case "prev": return t.prev_price == null ? null : Number(t.prev_price);
        case "curr": return t.curr_price == null ? null : Number(t.curr_price);
        case "delta_eur": return t.variation_eur == null ? null : Number(t.variation_eur);
        case "delta_pct": return t.variation_pct == null ? null : Number(t.variation_pct);
        case "status": {
          const order = { up: 0, down: 1, stable: 2, new: 3 };
          return order[t.status || "new"];
        }
        case "usage": return (p.linked_recipes || []).length;
        default: return null;
      }
    };
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = val(a), vb = val(b);
      // Valeurs nulles / manquantes toujours en fin
      const na = va == null || va === "";
      const nb = vb == null || vb === "";
      if (na && nb) return 0;
      if (na) return 1;
      if (nb) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "fr", { numeric: true }) * dir;
    });
  }, [products, filter, search, filters, sortKey, sortDir, suppliers]);

  const openHistory = async (p) => {
    setHistoryProduct(p);
    setHistory(await api.productHistory(p.id));
  };

  // Preview cost
  const preview = () => {
    const priceHT = form.price_is_ttc ? form.purchase_price / (1 + form.vat_rate / 100) : form.purchase_price;
    const total = form.purchase_quantity * form.pack_units;
    const usable = total * (form.yield_percent / 100) * (1 - form.loss_percent / 100);
    const family = ["kg", "g"].includes(form.purchase_unit) ? "weight" : ["L", "cl", "ml"].includes(form.purchase_unit) ? "volume" : "unit";
    const conv = { kg: 1000, g: 1, L: 1000, cl: 10, ml: 1, unité: 1 }[form.purchase_unit] || 1;
    const usableBase = usable * conv;
    const perBase = usableBase > 0 ? priceHT / usableBase : 0;
    if (family === "weight") return `${eur(perBase * 1000, 4)} / kg — ${eur(perBase, 4)} / g`;
    if (family === "volume") return `${eur(perBase * 1000, 4)} / L — ${eur(perBase, 4)} / ml`;
    return `${eur(perBase, 4)} / unité`;
  };

  return (
    <div className="space-y-5">
      <PageHero
        eyebrow="Base ingrédients"
        title="Produits & Ingrédients"
        subtitle={`${products.length} référence(s) · matières premières et prix fournisseurs`}
        pattern="receipt"
        action={
          <div className="flex items-center gap-2">
            <button
              data-testid="cleanup-products-btn"
              onClick={async () => {
                try {
                  const res = await api.cleanupProducts();
                  if (!res.pairs || res.pairs.length === 0) { toast.info("Aucun doublon détecté"); return; }
                  setReviewPairs({ open: true, pairs: res.pairs });
                } catch { toast.error("Impossible d'analyser les doublons"); }
              }}
              title="Détecter les doublons potentiels et demander une décision par paire"
              className="h-11 px-3 rounded-lg border border-[#CDD7E3] bg-white text-[13px] font-semibold text-[#0B1F33] inline-flex items-center gap-1.5 hover:bg-[#F4F5EF]"
            >
              <Wand2 className="w-4 h-4" /> Nettoyer les doublons
            </button>
            <button data-testid="new-product-btn" onClick={openNew} className="btn-orange !h-11">
              <Plus className="w-4 h-4" /> Nouveau produit
            </button>
          </div>
        }
      />

      <QuotaBar resource="products" label="Produits utilisés" />

      <div className="surface p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5E6875]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit, une marque…"
              data-testid="product-search"
              className="!h-10 pl-9 text-[13px]"
            />
          </div>
          {/* Ligne de filtres — une seule ligne, scroll horizontal tactile
              sur mobile, jamais de retour à la ligne. */}
          <div
            className="-mx-3 sm:mx-0 px-3 sm:px-0 overflow-x-auto scrollbar-none"
            data-testid="product-filters-scroll"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="flex items-center gap-1.5 whitespace-nowrap w-max">
              {filters.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setFilterAndUrl(f.key)}
                    data-testid={`product-filter-${f.key}`}
                    className={`h-8 px-2.5 rounded-md text-[12px] font-semibold border inline-flex items-center gap-1.5 transition-colors ${
                      active
                        ? "bg-[#FF9F1C] text-[#0B1F33] border-[#FF9F1C] shadow-[0_2px_6px_rgba(255,159,28,0.35)]"
                        : "bg-white text-[#0B1F33] border-[#CDD7E3] hover:bg-[#F4F5EF]"
                    }`}
                  >
                    {f.category
                      ? <CategoryIcon category={f.category} size={18} rounded="sm" className={active ? "!ring-1 !ring-[#0B1F33]/20" : ""} />
                      : null}
                    <span>{f.label}</span>
                    <span className={`tabular-nums text-[11px] font-mono-num ${active ? "text-[#0B1F33]/70" : "text-[#5E6875]"}`}>
                      {f.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="surface overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full dense">
              <thead>
                <tr>
                  <th className="w-[92px]"></th>
                  <SortableTh label="Produit" k="name" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                  <SortableTh label="Fournisseur" k="supplier" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                  <SortableTh label="Prix août" k="prev" align="num" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                  <SortableTh label="Prix sept." k="curr" align="num" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                  <SortableTh label="Δ €" k="delta_eur" align="num" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                  <SortableTh label="Δ %" k="delta_pct" align="num" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                  <SortableTh label="Statut" k="status" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                  <th>Courbe 3 mois</th>
                  <SortableTh label="Utilisé dans" k="usage" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 && (
                  <tr><td colSpan={10} className="text-center text-slate-500 py-8">Aucun produit ne correspond au filtre.</td></tr>
                )}
                {filteredProducts.map((p) => {
                  const t = p.trend || {};
                  const status = t.status || "new";
                  const StatusIcon = status === "up" ? TrendingUp : status === "down" ? TrendingDown : Minus;
                  const statusColor = status === "up" ? "text-[#D84343]" : status === "down" ? "text-[#24845B]" : "text-[#5E6875]";
                  const statusBg   = status === "up" ? "bg-[#FDECEC]" : status === "down" ? "bg-[#E4F1EB]" : "bg-[#F4F5EF]";
                  const statusLabel = status === "up" ? "En hausse" : status === "down" ? "En baisse" : status === "stable" ? "Stable" : "Nouveau";
                  const sparkColor = status === "up" ? "#D84343" : status === "down" ? "#24845B" : "#5E6875";
                  const series = (t.history_series || []).map((h) => h.value);
                  const sup = suppliers.find((s) => s.id === p.supplier_id);
                  return (
                    <tr
                      key={p.id}
                      data-testid={`product-row-${p.id}`}
                      onClick={() => setDetailId(p.id)}
                      className="cursor-pointer hover:bg-[#F4F5EF] transition-colors"
                    >
                      {/* Feb 2026 §« crayon hors écran sur mobile 400px » —
                          Le crayon a été déplacé dans la PREMIÈRE cellule
                          (colonne catégorie visible dès le premier pixel du
                          scroll horizontal), superposé à l'icône via un
                          layout flex compact. Taille tactile 32×32,
                          stopPropagation strict pour ne pas déclencher
                          l'ouverture de la fiche produit. */}
                      <td className="!py-2 !pl-2">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                            data-testid={`product-edit-${p.id}`}
                            title="Modifier le produit"
                            aria-label={`Modifier le produit ${p.name}`}
                            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#CDD7E3] text-[#5E6875] hover:text-[#1769E0] hover:border-[#1769E0] hover:bg-[#DCEBFF] transition-colors"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPendingDelete(p); }}
                            data-testid={`product-delete-${p.id}`}
                            title="Supprimer le produit"
                            aria-label={`Supprimer le produit ${p.name}`}
                            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#CDD7E3] text-[#5E6875] hover:text-red-600 hover:border-red-500 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <CategoryIcon category={p.category} size={40} rounded="md" />
                        </div>
                      </td>
                      <td className="font-medium">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDetailId(p.id); }}
                          data-testid={`product-open-${p.id}`}
                          className="w-full text-left text-[#0B1F33] hover:text-[#1769E0] hover:underline decoration-2 underline-offset-2 min-w-0"
                        >
                          <span className="block truncate">{p.name}</span>
                          <div className="text-[11px] text-[#5E6875] font-normal">{p.category}{p.brand ? ` · ${p.brand}` : ""}{p.content_value && p.content_unit ? ` · ${p.content_value} ${p.content_unit}` : ""}</div>
                        </button>
                      </td>
                      <td className="text-secondary">{t.curr_supplier_name || sup?.name || "—"}</td>
                      <td className="num">
                        {t.prev_price != null ? (
                          <>{eur(t.prev_price, 3)} <span className="text-slate-400">{String(t.unit || "").replace(/^€\s*\/?\s*/, "/")}</span></>
                        ) : "—"}
                      </td>
                      <td className="num">
                        {t.curr_price != null ? (
                          <>{eur(t.curr_price, 3)} <span className="text-slate-400">{String(t.unit || "").replace(/^€\s*\/?\s*/, "/")}</span></>
                        ) : "—"}
                      </td>
                      <td className={`num font-mono-num ${statusColor}`}>
                        {t.variation_eur != null ? `${t.variation_eur > 0 ? "+" : ""}${eur(t.variation_eur, 3)}` : "—"}
                      </td>
                      <td className={`num font-mono-num font-bold ${statusColor}`} data-testid={`product-trend-${p.id}`}>
                        {t.variation_pct != null ? `${t.variation_pct > 0 ? "+" : ""}${t.variation_pct.toFixed(1)} %` : "—"}
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold ${statusColor} ${statusBg}`}>
                          <StatusIcon className="w-3 h-3" strokeWidth={2.5} />
                          {statusLabel}
                        </span>
                      </td>
                      <td>
                        <Spark values={series} color={sparkColor} />
                      </td>
                      <td className="text-[11.5px] text-[#0B1F33]">
                        {(p.linked_recipes || []).length === 0 ? (
                          <span className="text-slate-400 italic">—</span>
                        ) : (
                          <span className="line-clamp-2">{(p.linked_recipes || []).map((r) => r.name).join(" · ")}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
      </div>

      {/* Product form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Nom du produit</Label>
              <Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fournisseur</Label>
              <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucun —</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Référence fournisseur</Label>
              <Input value={form.supplier_ref || ""} onChange={(e) => setForm({ ...form, supplier_ref: e.target.value })} />
            </div>
            <div>
              <Label>Prix d'achat</Label>
              <Input data-testid="product-form-price" type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} />
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label>Unité</Label>
                <Select value={form.purchase_unit} onValueChange={(v) => setForm({ ...form, purchase_unit: v })}>
                  <SelectTrigger data-testid="product-form-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{unitLabel(u)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch id="ttc" checked={form.price_is_ttc} onCheckedChange={(v) => setForm({ ...form, price_is_ttc: v })} />
                <Label htmlFor="ttc" className="text-sm">{form.price_is_ttc ? "TTC" : "HT"}</Label>
              </div>
            </div>
            <div>
              <Label>TVA</Label>
              <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{VAT_RATES.map((v) => <SelectItem key={v.value} value={String(v.value)}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantité par conditionnement</Label>
              <Input type="number" step="0.01" value={form.purchase_quantity} onChange={(e) => setForm({ ...form, purchase_quantity: e.target.value })} />
            </div>
            <div>
              <Label>Unités par conditionnement</Label>
              <Input type="number" value={form.pack_units} onChange={(e) => setForm({ ...form, pack_units: e.target.value })} />
            </div>
            <div>
              <Label>Prix de référence négocié</Label>
              <Input type="number" step="0.01" value={form.reference_price} onChange={(e) => setForm({ ...form, reference_price: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Allergènes présents dans le produit</Label>
            <p className="text-xs text-slate-500 mb-2">Cochez ceux qui composent le produit — ils seront agrégés automatiquement sur chaque recette qui l'utilise.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
              {ALLERGENS.map((a) => {
                const on = (form.allergens || []).includes(a.code);
                return (
                  <button
                    key={a.code}
                    type="button"
                    data-testid={`product-allergen-${a.code}`}
                    onClick={() => toggleAllergen(a.code)}
                    className={`text-xs px-2 py-1.5 rounded-md border transition-colors text-left ${on ? "bg-orange-50 border-orange-300 text-orange-800" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                  >
                    <span className="font-mono-num font-semibold mr-1">{a.code}</span>
                    <span>{a.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="rounded-md bg-[#DCEBFF] border border-[#DCEBFF] p-3 mt-2">
            <div className="text-xs text-slate-600">Prévisualisation du coût utilisable</div>
            <div data-testid="product-form-preview" className="font-mono-num font-semibold text-[#0B1F33] mt-1">{preview()}</div>
          </div>
          <DialogFooter className="sticky bottom-0 bg-white pt-3 pb-1 -mx-6 px-6 border-t border-slate-100">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} data-testid="product-form-cancel">Annuler</Button>
            <Button
              data-testid="product-form-save"
              onClick={() => save(false)}
              disabled={saving}
              className="bg-[#2878FF] hover:bg-[#1769E0] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyProduct} onOpenChange={(v) => !v && setHistoryProduct(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Historique des prix — {historyProduct?.name}</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {history.length === 0 ? <p className="text-sm text-slate-500">Aucun historique.</p> : (
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Date</TableHead><TableHead>Prix HT</TableHead><TableHead>Unité</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}><TableCell className="text-sm">{dateFR(h.date)}</TableCell><TableCell className="font-mono-num">{eurHT(h.price_ht)}</TableCell><TableCell>{h.purchase_unit}</TableCell><TableCell className="text-slate-600 text-sm">{h.source}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Product detail dialog */}
      <ProductDeleteDialog product={pendingDelete} onCancel={() => setPendingDelete(null)} onConfirm={remove} />
      {detailId && <ProductDetail productId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}

      {/* Duplicate detection dialog */}
      <DuplicateDialog
        open={!!duplicates}
        candidates={duplicates?.candidates || []}
        draft={duplicates?.draft}
        onCancel={() => setDuplicates(null)}
        onKeepSeparate={() => { setDuplicates(null); save(true); }}
        onUseExisting={(c) => { setDuplicates(null); setOpen(false); setDetailId(c.id); }}
      />

      {/* Revue manuelle des doublons potentiels — flux sûr, per-pair */}
      <DuplicateReviewDialog
        open={!!reviewPairs?.open}
        kind="product"
        pairs={reviewPairs?.pairs || []}
        onClose={() => setReviewPairs(null)}
        onCompleted={() => {
          window.dispatchEvent(new CustomEvent("rentascan:usage-changed", { detail: { resource: "products" } }));
          load();
        }}
      />

      {/* Feb 2026 — Dossier de négociation par produit */}
      <Dialog open={!!productDossier} onOpenChange={(v) => !v && setProductDossier(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-negotiation-dossier">
          {productDossier && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <FileText className="w-5 h-5 text-[#FF9F1C]" />
                  Dossier de négociation — {productDossier.product?.name}
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-slate-50 p-3 rounded"><div className="text-xs text-slate-500 uppercase">Variation</div><div className="font-mono-num font-semibold text-orange-700 mt-1">{pct(productDossier.variation_pct)}</div></div>
                <div className="bg-slate-50 p-3 rounded"><div className="text-xs text-slate-500 uppercase">Gain annuel estimé</div><div className="font-mono-num font-semibold text-red-700 mt-1">{productDossier.gain_potentiel_estime?.total_display || "Non estimé"}</div></div>
                <div className="bg-slate-50 p-3 rounded"><div className="text-xs text-slate-500 uppercase">Dépense annuelle estimée</div><div className="font-mono-num font-semibold mt-1">{productDossier.monthly_usage_base > 0 ? eur(productDossier.annual_spend) : "Non estimée"}</div></div>
                <div className="bg-slate-50 p-3 rounded"><div className="text-xs text-slate-500 uppercase">Prix cible HT</div><div className="font-mono-num font-semibold text-[#1769E0] mt-1">{productDossier.target_price_ht == null ? "Non établi" : `${eur(productDossier.target_price_ht)} /${productDossier.price_unit}`}</div></div>
              </div>

              {/* Gain potentiel estimé */}
              {productDossier.gain_potentiel_estime && (
                <div className="rounded-lg border border-[#FF9F1C]/40 bg-gradient-to-br from-[#FFF7E5] to-white p-4" data-testid="product-dossier-gain">
                  <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">Gain potentiel estimé</div>
                  {productDossier.gain_potentiel_estime.total_display ? (
                    <>
                      <div className="mt-1 font-mono-num text-[26px] font-extrabold text-[#0B1F33]">{productDossier.gain_potentiel_estime.total_display}</div>
                      <div className="text-[11.5px] text-[#5E6875] mt-1">{productDossier.gain_potentiel_estime.period_label}</div>
                    </>
                  ) : (
                    <div className="mt-1 text-[13px] text-[#5E6875]">
                      <Info className="w-3.5 h-3.5 inline-block mr-1 text-[#1769E0]" />
                      {productDossier.gain_potentiel_estime.notice}
                    </div>
                  )}
                  <div className="mt-2 text-[11.5px] text-[#5E6875] font-mono-num">
                    Prix actuel {eur(productDossier.gain_potentiel_estime.current_price_ht)} /{productDossier.gain_potentiel_estime.unit_base} · cible {eur(productDossier.gain_potentiel_estime.target_price_ht)} /{productDossier.gain_potentiel_estime.unit_base} · gain {eur(productDossier.gain_potentiel_estime.unit_saving_ht)} /{productDossier.gain_potentiel_estime.unit_base}
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-[#0B1F33] mb-2 flex items-center gap-2"><Mail className="w-4 h-4" /> Brouillon d'email</h4>
                <Textarea readOnly value={productDossier.email_body} rows={10} className="font-mono-num text-sm" data-testid="product-dossier-email" />
                <Button size="sm" variant="outline" className="mt-2 gap-2" onClick={() => copyText(productDossier.email_body)}><Copy className="w-4 h-4" /> Copier</Button>
              </div>

              <div>
                <h4 className="font-semibold text-[#0B1F33] mb-2 flex items-center gap-2"><Phone className="w-4 h-4" /> Trame d'appel</h4>
                <ol className="list-decimal list-inside text-sm text-slate-700 space-y-1 bg-slate-50 p-3 rounded">
                  {productDossier.phone_script.map((s, i) => <li key={i}>{s.replace(/^\d\.\s*/, "")}</li>)}
                </ol>
              </div>

              <div>
                <h4 className="font-semibold text-[#0B1F33] mb-2">Arguments</h4>
                <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
                  {productDossier.arguments.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
